import styled, { css, TColorProp } from 'styled-components';

import { SPACING } from '~/common/constants/spacing';
import { RADIUS } from '~/common/constants/radius';

import {
  TBorderRadius,
  TDropdownPosition,
  TDropdownSize,
  TDropdownVariant,
} from '../types';
import { getDropdownHeight, getDropdownPadding } from '../styles';

const GenericContainer = styled.div`
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
  background: ${({ theme }) => theme.container_background};
  border-radius: ${RADIUS._12};
  padding: ${SPACING._8};
  border: 1px solid ${({ theme }) => theme.neutral200};
`;

interface IOptionProps {
  $size: TDropdownSize;
  $isDisabled?: boolean;
  $variant: TDropdownVariant;
  $isSelected: boolean;
  $backgroundColor: TColorProp;
  $selectedBackgroundColor: TColorProp;
  $optionHoverBackgroundColor: TColorProp;
}

interface INoOptionsProps {
  $size: TDropdownSize;
}

interface IContainerProps {
  $isVisible: boolean;
  $containerClientRect: DOMRect;
  $position: TDropdownPosition;
}

interface IListWrapperProps {
  $isVisible: boolean;
  $inputWrapperClientRect: DOMRect;
  $position: TDropdownPosition;
}

interface IListContentProps {
  $isVisible: boolean;
  $position: TDropdownPosition | TBorderRadius;
  $backgroundColor: TColorProp;
  $borderColor: TColorProp;
}

export const Container = styled.div<IContainerProps>`
  position: absolute;
  height: ${({ $containerClientRect }) => $containerClientRect?.height || 0}px;
  width: ${({ $containerClientRect }) => $containerClientRect?.width || 0}px;
  left: ${({ $containerClientRect }) => $containerClientRect?.left || 0}px;
  top: ${({ $containerClientRect }) => $containerClientRect?.top || 0}px;

  transition: visibility ease-in 0.2s;

  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`;

export const ListWrapper = styled.div<IListWrapperProps>`
  position: absolute;
  height: ${({ $inputWrapperClientRect }) =>
    $inputWrapperClientRect?.height || 0}px;
  width: ${({ $inputWrapperClientRect }) =>
    $inputWrapperClientRect?.width || 0}px;
  left: ${({ $inputWrapperClientRect }) =>
    $inputWrapperClientRect?.left || 0}px;
  top: ${({ $inputWrapperClientRect }) => $inputWrapperClientRect?.top || 0}px;
  transition: visibility ease-in 0.2s;
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`;

export const ListContent = styled(GenericContainer)<IListContentProps>`
  position: absolute;
  margin-top: 4px;
  background-color: ${({ theme, $backgroundColor }) => theme[$backgroundColor]};
  border: ${({ theme, $borderColor }) => `1px solid ${theme[$borderColor]}`};
  width: 100%;
  overflow: hidden;
  z-index: 1002;
  transition: all ease-out 0.2s;
  opacity: 0;
  visibility: hidden;

  ${({ $position }) =>
    $position === 'bottom'
      ? css`
          border-bottom-left-radius: ${RADIUS._16};
          border-bottom-right-radius: ${RADIUS._16};
          top: 100%;
        `
      : $position === 'full'
      ? css`
          border-bottom-left-radius: ${RADIUS._16};
          border-bottom-right-radius: ${RADIUS._16};
          border-top-left-radius: ${RADIUS._16};
          border-top-right-radius: ${RADIUS._16};
          top: 100%;
        `
      : css`
          border-top-left-radius: ${RADIUS._16};
          border-top-right-radius: ${RADIUS._16};
          bottom: 100%;
        `}

  ${({ $isVisible }) =>
    $isVisible &&
    css`
      opacity: 1;
      visibility: visible;
    `}

  > ul {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    scrollbar-width: thin;
    overflow-x: hidden;
    gap: ${SPACING._4};
  }
`;

export const Option = styled.li<IOptionProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  min-height: ${({ $variant, $size }) =>
    $variant === 'compact' ? getDropdownHeight[$size] : '40px'};
  padding: ${({ $variant, $size }) =>
    $variant === 'compact'
      ? getDropdownPadding[$size]
      : `${SPACING._8} ${SPACING._16}`};
  gap: ${SPACING._8};
  background-color: ${({ theme, $backgroundColor }) => theme[$backgroundColor]};
  border-radius: ${RADIUS._12};

  cursor: pointer;

  transition: all ease-out 0.2s;

  ${({ $isSelected, theme, $selectedBackgroundColor }) =>
    $isSelected &&
    css`
      background-color: ${theme[$selectedBackgroundColor]};
    `}

  ${({ $isDisabled, theme, $optionHoverBackgroundColor }) =>
    $isDisabled
      ? css`
          cursor: not-allowed;
          opacity: 0.48;
        `
      : css`
          &:hover {
            background-color: ${theme[$optionHoverBackgroundColor]};
          }
        `}
`;

export const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING._4};

  flex: 1;

  > header {
    display: flex;
    align-items: center;
    gap: ${SPACING._4};
  }
`;

export const OptionLabelSublabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING._4};
  margin-right: auto;
`;

export const NoOptionsWrapper = styled.span<INoOptionsProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: ${({ $size }) => getDropdownHeight[$size]};
  text-align: center;
`;
